import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { isAuthorizedUser } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { GeoEventSlackChannel } from './useGeoEventSlackChannels.types';

type UseGeoEventSlackChannelsProps = {
  geoEventId?: number;
};

type UseGeoEventSlackChannels = Omit<
  UseQueryResult<GeoEventSlackChannel[], Error>,
  'data'
> & {
  slackChannels: GeoEventSlackChannel[];
};

const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;

const fetchGeoEventSlackChannels = async (
  geoEventId: number,
): Promise<GeoEventSlackChannel[]> => {
  const response = await API.get<GeoEventSlackChannel[]>(
    `geo_events/${geoEventId}/slack_channels/`,
  );
  return response.data;
};

export const useGeoEventSlackChannels = (
  props: UseGeoEventSlackChannelsProps,
): UseGeoEventSlackChannels => {
  const { geoEventId } = props;
  const {
    user,
    permissions: { isInternalUser },
  } = useAuthState();

  // The endpoint only exists under the authorized prefix, which api.ts adds for authorized users.
  const { data = [], ...results } = useQuery({
    queryKey: ['geoEventSlackChannels', geoEventId],
    queryFn: () => fetchGeoEventSlackChannels(geoEventId!),
    enabled: isInternalUser && isAuthorizedUser(user) && Boolean(geoEventId),
    staleTime: FIVE_MINUTES_IN_MS,
  });

  return { ...results, slackChannels: data };
};
